import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Deck.css';
import eienLogo from '../assets/eien_logo.png';
import CalendlyLink from '../components/global/CalendlyLink';
import EmailLink from '../components/global/EmailLink';
import WhatsAppLink from '../components/global/WhatsAppLink';
import { trackEvent } from '../lib/analytics';
import { CTA_IDS, CTA_LOCATIONS } from '../lib/analytics.constants';

export default function Deck({ lang = 'es' }) {
  const rootRef = useRef(null);
  const barRef = useRef(null);
  const orbRef = useRef(null);
  const trailRef = useRef(null);
  const [rotateHint, setRotateHint] = useState(false);

  const es = lang === 'es';
  const t = useCallback((esTxt, enTxt) => (es ? esTxt : enTxt), [es]);

  /* palabra clave: la luciérnaga la ilumina al pasar cerca */
  const K = ({ children }) => <span className="dk-key">{children}</span>;

  /* líneas con máscara (reveal estilo keynote) */
  const maskLines = (lines) =>
    lines.map((l, i) => (
      <span className="dk-line" key={i}>
        <span className="dk-line-in" style={{ '--ld': `${i * 0.1}s` }}>{l}</span>
      </span>
    ));

  /* ---------- hint de rotación: opcional, nunca bloquea ---------- */
  useEffect(() => {
    const mq = window.matchMedia('(orientation: portrait)');
    let timer;
    if (mq.matches && window.innerWidth < 820) {
      setRotateHint(true);
      timer = setTimeout(() => setRotateHint(false), 10000);
    }
    const onChange = (e) => { if (!e.matches) setRotateHint(false); };
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      clearTimeout(timer);
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);

  /* ---------- motor de scroll: etapas + reveals + barra ---------- */
  useEffect(() => {
    window.scrollTo(0, 0);
    const root = rootRef.current;
    if (!root) return;

    const sections = Array.from(root.querySelectorAll('.dk-sec')).map((sec) => {
      const posterItems = Array.from(sec.querySelectorAll('.dk-poster-item'));
      return {
        sec,
        stages: +(sec.dataset.stages || 1),
        els: Array.from(sec.querySelectorAll('.dk-anim')).map((el) => ({
          el,
          from: +(el.dataset.from || 0),
          until: el.dataset.until != null ? +el.dataset.until : Infinity,
        })),
        posterItems,
        posterScan: sec.querySelector('.dk-poster-scan'),
        posterClose: sec.querySelector('.dk-poster-close'),
      };
    });

    let ticking = false;
    const paint = () => {
      ticking = false;
      const vh = window.innerHeight;
      sections.forEach(({ sec, stages, els, posterItems, posterScan, posterClose }) => {
        const r = sec.getBoundingClientRect();
        if (r.top < vh * 0.62) sec.classList.add('dk-vis');
        /* progreso continuo para efectos scrubbed (parallax, salida del hero) */
        const enter = Math.min(1, Math.max(0, (vh - r.top) / (vh * 0.9)));
        const exit = Math.min(1, Math.max(0, -r.top / (r.height * 0.9)));
        sec.style.setProperty('--enter', enter.toFixed(3));
        sec.style.setProperty('--exit', exit.toFixed(3));
        let stage = 0;
        if (stages > 1) {
          const total = Math.max(1, r.height - vh);
          const p = Math.min(0.999, Math.max(0, -r.top / total));
          stage = Math.min(stages - 1, Math.floor(p * stages));
          if (sec.dataset.stage !== String(stage)) sec.dataset.stage = String(stage);
        }
        const vis = sec.classList.contains('dk-vis');
        els.forEach(({ el, from, until }) => {
          el.classList.toggle('dk-on', vis && stage >= from && stage <= until);
          el.classList.toggle('dk-out', vis && stage > until);
        });

        if (sec.classList.contains('dk-s07')) {
          const nodes = sec.querySelectorAll('.dk-loop-node');
          const walking = stage <= 5;
          const active = walking ? stage : -1;
          nodes.forEach((node) => {
            const step = +(node.dataset.step || 0);
            node.classList.toggle('dk-loop-lit', walking && step === active);
            node.classList.toggle('dk-loop-done', walking && step < active);
            node.classList.toggle('dk-loop-wait', walking && step > active);
          });
        }

        if (posterItems.length) {
          const total = Math.max(1, r.height - vh);
          const progress = Math.min(1, Math.max(0, -r.top / total));
          const count = posterItems.length;
          const revealSpan = 0.56;
          const revealDur = 0.09;
          const departBase = 0.84;
          const departSpan = 0.11;
          const shortLand = window.matchMedia('(orientation: landscape) and (max-height: 560px)').matches;
          const xTravel = shortLand ? 24 : 72;

          posterItems.forEach((item, i) => {
            const t = count > 1 ? i / (count - 1) : 0;
            const revealStart = 0.03 + t * revealSpan;
            const reveal = Math.min(1, Math.max(0, (progress - revealStart) / revealDur));
            const departStart = departBase + t * departSpan;
            const depart = Math.min(1, Math.max(0, (progress - departStart) / 0.07));
            const opacity = reveal * (1 - depart);
            const direction = item.dataset.motionFrom === 'right' ? 1 : -1;
            const x = (1 - reveal) * xTravel * direction;
            const y = depart * -24;
            const blur = (1 - reveal) * 6 + depart * 5;
            const clip = (1 - reveal) * 100;
            const flash = Math.max(0, 1 - Math.abs(reveal - 0.62) / 0.38) * (1 - depart);

            item.style.opacity = opacity.toFixed(3);
            item.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
            item.style.filter = `blur(${blur.toFixed(2)}px) brightness(${(1 + flash * 0.55).toFixed(2)})`;
            item.style.color = `color-mix(in srgb, var(--poster-color) ${(100 - flash * 100).toFixed(1)}%, var(--signal))`;
            item.style.textShadow = `0 0 ${(flash * 18).toFixed(1)}px rgba(61, 219, 132, ${(flash * 0.72).toFixed(3)})`;
            item.style.clipPath = item.dataset.motionFrom === 'right'
              ? `inset(0 0 0 ${clip.toFixed(2)}%)`
              : `inset(0 ${clip.toFixed(2)}% 0 0)`;
          });

          if (posterScan) {
            const scan = Math.min(1, Math.max(0, (progress - 0.02) / 0.72));
            const scanOpacity = Math.max(0, Math.min(0.72, scan * 4, (1 - scan) * 5));
            posterScan.style.opacity = scanOpacity.toFixed(3);
            posterScan.style.transform = `translate3d(0, ${(scan * 318).toFixed(1)}px, 0) scaleX(${(0.08 + scan * 0.92).toFixed(3)})`;
          }

          if (posterClose) {
            const close = Math.min(1, Math.max(0, (progress - 0.76) / 0.14));
            posterClose.style.opacity = close.toFixed(3);
            posterClose.style.transform = `translate3d(0, ${((1 - close) * 24).toFixed(1)}px, 0)`;
            posterClose.style.filter = `blur(${((1 - close) * 5).toFixed(2)}px)`;
            posterClose.style.clipPath = `inset(${((1 - close) * 100).toFixed(2)}% 0 0 0)`;
          }
        }
      });
      if (barRef.current) {
        const max = Math.max(1, document.documentElement.scrollHeight - vh);
        const p = Math.min(1, window.scrollY / max);
        barRef.current.style.transform = `scaleX(${p})`;
        root.style.setProperty('--sp', p.toFixed(4));
      }
    };

    /* escala los lienzos de diseño fijo (loop, poster, equipo) */
    const fit = () => {
      const compact = window.matchMedia('(max-width: 819px)').matches;
      const shortLand = window.matchMedia('(orientation: landscape) and (max-height: 560px)').matches;
      root.querySelectorAll('.dk-fitbox').forEach((box) => {
        const inner = box.firstElementChild;
        if (!inner) return;
        if ((compact || shortLand) && box.classList.contains('dk-reflow')) {
          box.style.height = '';
          inner.style.width = '';
          inner.style.height = '';
          inner.style.transform = '';
          return;
        }
        const w = +box.dataset.w;
        const h = +box.dataset.h;
        const s = Math.min(1, box.clientWidth / w);
        box.style.height = `${h * s}px`;
        inner.style.width = `${w}px`;
        inner.style.height = `${h}px`;
        inner.style.transform = `translateX(-50%) scale(${s})`;
      });
    };

    const onScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(paint); }
    };
    const onResize = () => { fit(); onScroll(); };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    fit();
    paint();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [lang]);

  useEffect(() => {
    trackEvent('deck_view', { deck_lang: lang });
  }, [lang]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;

    const storageKey = `eien_deck_sections_${lang}`;
    const readSeen = () => {
      try {
        return new Set(JSON.parse(window.sessionStorage.getItem(storageKey) || '[]'));
      } catch {
        return new Set();
      }
    };
    const writeSeen = (seen) => {
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(Array.from(seen)));
      } catch {
        // Session storage is best-effort; duplicate protection still works in memory.
      }
    };

    const seen = readSeen();
    const sections = Array.from(root.querySelectorAll('.dk-sec'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = sections.indexOf(entry.target);
          if (index < 0) return;
          const deckSection = String(index + 1).padStart(2, '0');
          if (seen.has(deckSection)) return;
          seen.add(deckSection);
          writeSeen(seen);
          trackEvent('deck_section_view', {
            deck_lang: lang,
            deck_section: deckSection,
          });
        });
      },
      { threshold: 0.5 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [lang]);

  /* ---------- luciérnaga: luz errante con estela curva e imán a keywords ---------- */
  useEffect(() => {
    const orb = orbRef.current;
    const canvas = trailRef.current;
    const root = rootRef.current;
    if (!orb || !canvas || !root) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ctx = canvas.getContext('2d');
    const keys = Array.from(root.querySelectorAll('.dk-key, .dk-magnet'));
    const hist = [];
    let raf;
    let x = null;
    let y = null;
    let dpr = 1;

    const sizeCanvas = () => {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
    };
    sizeCanvas();
    window.addEventListener('resize', sizeCanvas);

    const loop = (tms) => {
      const t = tms / 1000;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const max = Math.max(1, document.documentElement.scrollHeight - vh);
      const p = Math.min(1, window.scrollY / max);

      /* camino base ondulado + vagabundeo pseudoaleatorio (ondas desfasadas) */
      const sway = Math.sin(p * Math.PI * 6 + Math.sin(p * Math.PI * 2.3) * 1.6);
      const wanderX = (Math.sin(t * 0.37) + 0.6 * Math.sin(t * 0.83 + 1.4) + 0.35 * Math.sin(t * 1.31 + 4.2)) * 42;
      const wanderY = (Math.cos(t * 0.31) + 0.6 * Math.sin(t * 0.57 + 0.6) + 0.35 * Math.cos(t * 1.13 + 2.8)) * 32;
      let tx = vw * (0.5 + 0.36 * sway) + wanderX;
      let ty = vh * (0.07 + 0.86 * p) + wanderY;

      /* imán: si hay una palabra clave visible cerca, la luz se desvía y la ilumina */
      let best = null;
      let bestD = 340;
      keys.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh || r.width === 0) {
          el.style.setProperty('--k', '0');
          return;
        }
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const d = Math.hypot(cx - (x ?? tx), cy - (y ?? ty));
        el.style.setProperty('--k', Math.max(0, 1 - d / 210).toFixed(2));
        if (d < bestD) { bestD = d; best = { cx, cy }; }
      });
      if (best) {
        const w = (1 - bestD / 340) * 0.6;
        tx = tx * (1 - w) + best.cx * w;
        ty = ty * (1 - w) + (best.cy - 30) * w; /* se posa apenas por encima de la palabra */
      }

      if (x === null) { x = tx; y = ty; }
      const px = x;
      const py = y;
      x += (tx - x) * 0.05;
      y += (ty - y) * 0.05;
      const speed = Math.hypot(x - px, y - py);

      hist.push({ x, y });
      if (hist.length > 34) hist.shift();

      /* estela: curva suave por los puntos del recorrido, se apaga hacia la cola */
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, vw, vh);
      const glow = Math.min(1, speed / 4.5);
      if (hist.length > 2 && glow > 0.03) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (let i = 1; i < hist.length - 1; i++) {
          const k = i / (hist.length - 1);
          const x0 = (hist[i - 1].x + hist[i].x) / 2;
          const y0 = (hist[i - 1].y + hist[i].y) / 2;
          const x1 = (hist[i].x + hist[i + 1].x) / 2;
          const y1 = (hist[i].y + hist[i + 1].y) / 2;
          /* pasada ancha difusa + pasada fina brillante */
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.quadraticCurveTo(hist[i].x, hist[i].y, x1, y1);
          ctx.strokeStyle = `rgba(61,219,132,${(0.14 * k * glow).toFixed(3)})`;
          ctx.lineWidth = 7;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.quadraticCurveTo(hist[i].x, hist[i].y, x1, y1);
          ctx.strokeStyle = `rgba(61,219,132,${(0.6 * k * glow).toFixed(3)})`;
          ctx.lineWidth = 1.7;
          ctx.stroke();
        }
      }

      orb.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', sizeCanvas);
    };
  }, [lang]);

  /* ---------- contenido ---------- */
  /* posiciones pensadas para no pisar el texto fijo del slide:
     franja superior izquierda (body) y centro izquierda (hero) quedan libres */
  /* w: oleada en la que aparece (1..3) · d: delay dentro de su oleada
     x/y: posición desktop (filas sin colisión, texto en una línea)
     mx/my: posición móvil (solo las visibles; columnas izq/der alrededor del titular) */
  const questions = [
    { es: '¿Qué deberíamos automatizar?', en: 'What should we automate?', x: '54%', y: '5%', mx: '50%', my: '3%', lx: '62%', ly: '6%', s: 24, o: 0.55, r: -1.2, dur: 9, w: 1, d: 0.1 },
    { es: '¿Dónde estamos perdiendo plata?', en: 'Where are we losing money?', x: '70%', y: '14%', mx: '50%', my: '12%', lx: '62%', ly: '20%', s: 18, o: 0.4, r: 0, dur: 11, w: 1, d: 0.3 },
    { es: '¿Qué podría convertirse en un producto?', en: 'What could become a product?', x: '56%', y: '24%', mx: '50%', my: '21%', lx: '62%', ly: '34%', s: 26, o: 0.7, r: 0.8, dur: 8, sig: true, w: 2, d: 0.1 },
    { es: '¿Comprar o construir?', en: 'Build or buy?', x: '86%', y: '46%', s: 16, o: 0.35, r: 0, dur: 13, hide: true, w: 1, d: 0.5 },
    { es: '¿Dónde puede ayudar de verdad la IA?', en: 'Where can AI actually help?', x: '68%', y: '50%', mx: '50%', my: '64%', lx: '62%', ly: '66%', s: 22, o: 0.5, r: -0.6, dur: 10, w: 2, d: 0.3 },
    { es: '¿Qué NO deberíamos construir?', en: 'What should we NOT build?', x: '58%', y: '68%', mx: '4%', my: '64%', lx: '4%', ly: '66%', s: 28, o: 0.75, r: 0.5, dur: 9, sig: true, w: 3, d: 0.1 },
    { es: '¿Por qué mantener esto cuesta tanto?', en: 'Why is this so expensive to maintain?', x: '58%', y: '80%', s: 16, o: 0.32, r: 0, dur: 12, hide: true, w: 2, d: 0.5 },
    { es: '¿Qué están lanzando nuestros competidores?', en: 'What are our competitors shipping?', x: '4%', y: '78%', s: 19, o: 0.42, r: -0.8, dur: 11, hide: true, w: 1, d: 0.7 },
    { es: '¿Esto escala si crecemos 10×?', en: 'Does this scale at 10×?', x: '8%', y: '84%', mx: '4%', my: '75%', lx: '4%', ly: '80%', s: 23, o: 0.55, r: 0.6, dur: 10, w: 2, d: 0.7 },
    { es: '¿Qué deberíamos construir primero?', en: 'What should we build first?', x: '36%', y: '92%', s: 18, o: 0.4, r: 0, dur: 13, hide: true, w: 3, d: 0.3 },
    { es: '¿Otra app más?', en: 'Another app?', x: '88%', y: '6%', s: 16, o: 0.35, r: 0, dur: 12, hide: true, w: 2, d: 0.85 },
    { es: '¿Por qué seguimos usando Excel para esto?', en: 'Why are we still running this on Excel?', x: '6%', y: '12%', s: 17, o: 0.38, r: -0.5, dur: 11, hide: true, w: 1, d: 0.9 },
    { es: '¿Cuánto cuesta no hacer nada?', en: 'What does doing nothing cost?', x: '72%', y: '36%', mx: '4%', my: '21%', lx: '4%', ly: '20%', s: 20, o: 0.5, r: 0.7, dur: 10, w: 3, d: 0.5 },
    { es: '¿Quién es dueño de los datos?', en: 'Who owns the data?', x: '84%', y: '58%', s: 15, o: 0.3, r: 0, dur: 13, hide: true, w: 3, d: 0.65 },
    { es: '¿Esto lo resuelve una IA?', en: 'Is AI the answer here?', x: '74%', y: '74%', s: 21, o: 0.5, r: -0.5, dur: 9, hide: true, w: 1, d: 1.05 },
    { es: '¿Por qué el CRM no habla con la facturación?', en: "Why doesn't the CRM talk to billing?", x: '6%', y: '70%', s: 15, o: 0.3, r: 0, dur: 12, hide: true, w: 3, d: 0.8 },
    { es: '¿En qué invertimos primero?', en: 'Where do we invest first?', x: '6%', y: '92%', mx: '4%', my: '85%', lx: '4%', ly: '6%', s: 18, o: 0.42, r: 0.4, dur: 10, w: 2, d: 1.0 },
    { es: '¿Cómo medimos el retorno?', en: 'How do we measure return?', x: '64%', y: '88%', mx: '50%', my: '85%', lx: '62%', ly: '80%', s: 24, o: 0.6, r: -0.9, dur: 9, sig: true, w: 3, d: 0.95 },
  ];

  const loopNodes = [
    { x: 270, y: 30, es: ['Identificar', 'Dónde está la oportunidad.'], en: ['Identify', 'Where the opportunity is.'] },
    { x: 730, y: 448, es: ['Localizar', 'Qué está frenando el resultado.'], en: ['Locate', "What's holding back the result."] },
    { x: 968, y: 240, es: ['Diseñar & Decidir', 'La menor apuesta capaz de moverlo.'], en: ['Design & Decide', 'The smallest bet that can move it.'] },
    { x: 730, y: 30, es: ['Construir', 'Lanzarlo en el mundo real.'], en: ['Build', 'Ship it in the real world.'] },
    { x: 270, y: 448, es: ['Medir', 'Demostrar el impacto.'], en: ['Measure', 'Prove the impact.'] },
    { x: 30, y: 240, es: ['Activar', 'Ponerla a trabajar.'], en: ['Activate', 'Put it to work.'] },
  ];

  const cards = [
    { h: t('PROYECTO', 'PROJECT'), p: t('Tecnología construida para un desafío concreto.', 'Technology built for a defined challenge.') },
    { h: 'PARTNERSHIP', p: t('Evolución tecnológica continua.', 'Continuous technology evolution.') },
    { h: 'REVENUE SHARE', p: t('Lo construimos. Crecemos con él.', 'We build it. We grow with it.'), featured: true },
  ];

  const poster = [
    { es: 'Eliminar trabajo manual', en: 'Remove manual work', style: { left: 0, top: 8, fontSize: 40 }, emphasis: 'primary', from: 'left' },
    { es: 'Reducir tiempos de respuesta', en: 'Reduce response times', style: { right: 0, top: 8, fontSize: 40 }, emphasis: 'primary', from: 'right' },
    { es: 'Conectar áreas aisladas', en: 'Connect isolated teams', style: { left: 0, top: 68, fontSize: 40 }, emphasis: 'primary', from: 'left' },
    { es: 'Aprovechar datos que hoy no usamos', en: "Use data we're not using today", style: { right: 0, top: 66, fontSize: 40 }, emphasis: 'primary', from: 'right' },
    { es: 'Reducir costos operativos', en: 'Reduce operating costs', style: { left: 0, top: 128, fontSize: 40 }, emphasis: 'primary', from: 'left' },
    { es: 'Mejorar la experiencia del cliente', en: 'Improve the customer experience', style: { right: 0, top: 132, fontSize: 40 }, emphasis: 'primary', from: 'right' },
    { es: 'Aumentar conversión', en: 'Increase conversion', style: { left: 0, top: 196, fontSize: 40 }, emphasis: 'primary', from: 'left' },
    { es: 'Crear nuevas fuentes de ingresos', en: 'Create new revenue streams', style: { right: 0, top: 202, fontSize: 40 }, emphasis: 'primary', from: 'right' },
    { es: 'Escalar sin aumentar estructura', en: 'Scale without adding structure', style: { left: 0, top: 264, fontSize: 40 }, emphasis: 'primary', from: 'left' },
  ];

  const teamNodes = [
    { es: 'PRODUCTO', en: 'PRODUCT', pos: { left: '50%', top: '14.3%' } },
    { es: 'IA', en: 'AI', pos: { left: '81.4%', top: '35.7%' } },
    { es: 'ESTRATEGIA', en: 'STRATEGY', pos: { left: '71.4%', top: '80.4%' } },
    { es: 'INGENIERÍA', en: 'ENGINEERING', pos: { left: '28.6%', top: '80.4%' } },
    { es: 'DISEÑO', en: 'DESIGN', pos: { left: '18.6%', top: '35.7%' } },
  ];

  const loopPath = 'M 150,150 C 150,30 390,30 500,150 C 610,270 850,270 850,150 C 850,30 610,30 500,150 C 390,270 150,270 150,150 Z';

  const title = t('eien — Presentación', 'eien — Deck');
  const desc = t(
    'Toda empresa se está convirtiendo en una empresa de tecnología. Pero no todas saben que tecnología construir primero.',
    'Every company is becoming a technology company. But not all know what technology to build first.'
  );

  return (
    <div className="dk-root" ref={rootRef} data-lang={lang}>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={es ? 'https://eientech.io/deck' : 'https://eientech.io/deck/en'} />
        <meta property="og:image" content={es ? 'https://eientech.io/og-deck.jpg' : 'https://eientech.io/og-deck-en.jpg'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={es ? 'https://eientech.io/og-deck.jpg' : 'https://eientech.io/og-deck-en.jpg'} />
        <link rel="canonical" href={es ? 'https://eientech.io/deck' : 'https://eientech.io/deck/en'} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* ---------- 01 · apertura ---------- */}
      <section className="dk-sec dk-multi dk-s01">
        <div className="dk-stage">
          <div className="dk-frame">
            <div className="dk-topbar">
              <Link className="dk-homelink" to="/">← {t('Volver al sitio', 'Back to site')}</Link>
              <nav className="dk-langsw" aria-label={t('Idioma', 'Language')}>
                <Link to="/deck" className={es ? 'dk-lang-act' : ''}>ES</Link>
                <span>/</span>
                <Link to="/deck/en" className={!es ? 'dk-lang-act' : ''}>EN</Link>
              </nav>
            </div>
            <div className="dk-mid">
              <div className="dk-s01-main">
                <h1 className="dk-hero dk-anim dk-mask">
                  {maskLines(t(
                    ['Toda empresa', 'se está convirtiendo', <>en una empresa de <K>tecnología</K>.</>],
                    ['Every company', 'is becoming', <>a <K>technology</K> company.</>]
                  ))}
                </h1>
              </div>
              <div className="dk-s01-sub">
                <p className="dk-hero-sub dk-anim dk-mask" style={{ '--d': '.45s' }}>
                  {maskLines(t(
                    ['Pero no todas saben qué tecnología', <>construir <span className="dk-sub-hot">primero</span>.</>],
                    ['But not all know what technology', <>to build <span className="dk-sub-hot">first</span>.</>]
                  ))}
                </p>
              </div>
            </div>
            <div className="dk-bot">
              <div className="dk-body dk-dim dk-anim" style={{ '--d': '.35s' }}>
                {t('Algunas todavía no se dieron cuenta.', "Some just haven't realized it yet.")}
              </div>
              <div className="dk-wordmark">eien</div>
            </div>
            <div className="dk-scrollcue dk-anim" style={{ '--d': '1.1s' }} aria-hidden="true">
              <span>{t('scrolleá', 'scroll')}</span>
              <i />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 02 · puente: el riesgo equivocado ---------- */}
      <section className="dk-sec dk-multi dk-s02" data-stages="2" data-stage="0">
        <div className="dk-stage">
          <div className="dk-frame">
            <div className="dk-mid">
              <h2 className="dk-statement dk-anim dk-mask" data-until="0">
                {maskLines(t(
                  ['El riesgo no es quedarse atrás.', <>Es invertir en la <em className="dk-em dk-key">tecnología equivocada</em>.</>],
                  ["The risk isn't falling behind.", <>It's investing in the <em className="dk-em dk-key">wrong technology</em>.</>]
                ))}
              </h2>
              <div className="dk-bridge-body dk-anim" data-until="0" style={{ '--d': '.5s' }}>
                <span className="dk-bridge-opts">
                  {t('IA. Automatización. Software interno. Nuevos productos. Integraciones. Datos.',
                     'AI. Automation. Internal software. New products. Integrations. Data.')}
                </span>
              </div>
            </div>
            <div className="dk-center-abs">
              <h2 className="dk-hero dk-anim dk-mask" data-from="1" style={{ '--d': '.3s' }}>
                {maskLines(t(
                  ['Las oportunidades son infinitas.', <><em className="dk-em">Los recursos no.</em></>],
                  ['The opportunities are endless.', <><em className="dk-em">The resources aren't.</em></>]
                ))}
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 03 · el problema de decisión ---------- */}
      <section className="dk-sec dk-multi dk-s03" data-stages="4" data-stage="0">
        <div className="dk-stage">
          <div className="dk-frame">
            <div className="dk-noise" aria-hidden="true">
              {questions.map((q, i) => (
                <span
                  key={i}
                  className={`dk-q dk-anim${q.sig ? ' dk-q-sig' : ''}${q.hide ? ' dk-q-hide' : ''}${q.hideLand ? ' dk-q-hide-land' : ''}`}
                  data-from={q.w}
                  style={{
                    left: q.x,
                    top: q.y,
                    '--mx': q.mx,
                    '--my': q.my,
                    '--lx': q.lx,
                    '--ly': q.ly,
                    fontSize: q.s,
                    rotate: q.r ? `${q.r}deg` : undefined,
                    '--o': q.o,
                    '--dur': `${q.dur}s`,
                    '--dx': `${((i % 3) - 1) * 3 || 2}px`,
                    '--dy': `${(((i + 1) % 3) - 1) * 3 || -2}px`,
                    '--d': `${q.d}s`,
                  }}
                >
                  {t(q.es, q.en)}
                </span>
              ))}
            </div>
            <div className="dk-top">
              <div className="dk-s03-lead dk-anim" data-until="0">
                {t('La mayoría de las empresas no tienen un problema de software.',
                   "Most companies don't have a software problem.")}
              </div>
            </div>
            <div className="dk-mid">
              <h2 className="dk-hero dk-anim dk-mask" style={{ '--d': '.25s' }}>
                {maskLines(t(
                  ['Tienen un problema', <>de <K>decisión</K>.</>],
                  ['They have a', <><K>decision</K> problem.</>]
                ))}
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 04 · la fórmula ---------- */}
      <section className="dk-sec dk-s04">
        <div className="dk-frame">
          <div className="dk-mid dk-s04-mid">
            <h2 className="dk-enter dk-anim" style={{ '--d': '.1s' }}>
              {t(
                <>Ahí es donde entra <em className="dk-enter-name">eien</em>.</>,
                <>That's where <em className="dk-enter-name">eien</em> comes in.</>
              )}
            </h2>
            <div className="dk-formula">
              <span className="dk-anim" style={{ '--d': '.45s' }}>{t('Estrategia', 'Strategy')}</span>
              <span className="dk-x dk-anim" style={{ '--d': '.55s' }}>×</span>
              <span className="dk-anim" style={{ '--d': '.65s' }}>{t('Tecnología', 'Technology')}</span>
              <span className="dk-x dk-anim" style={{ '--d': '.75s' }}>×</span>
              <span className="dk-anim" style={{ '--d': '.85s' }}>{t('Ejecución', 'Execution')}</span>
            </div>
          </div>
        </div>
      </section>


      {/* ---------- 07 · el loop ---------- */}
      <section className="dk-sec dk-multi dk-s07" data-stages="8" data-stage="0">
        <div className="dk-stage">
          <div className="dk-frame">
            <div className="dk-top">
              <div className="dk-micro dk-dim dk-anim" data-until="5">{t('永遠 — sin final', '永遠 — without end')}</div>
              <h2 className="dk-title dk-anim" data-until="5" style={{ '--d': '.1s', marginTop: 14 }}>
                {t('El Loop de eien', 'The eien Loop')}
              </h2>
              <div className="dk-loop-lead dk-anim" data-until="0" style={{ '--d': '.25s' }}>
                {t('No contratás un proveedor. Iniciás una alianza.',
                   "You don't hire a provider. You start a partnership.")}
              </div>
            </div>
            <div className="dk-mid">
              <div className="dk-fitbox dk-reflow dk-anim" data-until="5" data-w="1000" data-h="478">
                <div className="dk-loop">
                  <svg viewBox="0 0 1000 300" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <filter id="dk-loopglow" x="-200%" y="-200%" width="500%" height="500%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="soft" />
                        <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="mid" />
                        <feGaussianBlur in="SourceGraphic" stdDeviation="18" result="wide" />
                        <feMerge>
                          <feMergeNode in="wide" />
                          <feMergeNode in="mid" />
                          <feMergeNode in="soft" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                      <radialGradient id="dk-orbcore" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#eafff3" />
                        <stop offset="45%" stopColor="#3DDB84" />
                        <stop offset="100%" stopColor="#1a9a56" />
                      </radialGradient>
                    </defs>
                    <path className="dk-draw" pathLength="1" d={loopPath} />
                    <g className="dk-loop-orb">
                      <circle className="dk-loop-beam" r="22" fill="rgba(61,219,132,0.18)">
                        <animateMotion dur="9s" repeatCount="indefinite" path={loopPath} />
                      </circle>
                      <circle className="dk-loop-beam-core" r="10" fill="rgba(61,219,132,0.45)" filter="url(#dk-loopglow)">
                        <animateMotion dur="9s" repeatCount="indefinite" path={loopPath} />
                      </circle>
                      <circle r="5" fill="url(#dk-orbcore)" filter="url(#dk-loopglow)">
                        <animateMotion dur="9s" repeatCount="indefinite" path={loopPath} />
                      </circle>
                    </g>
                  </svg>
                  {loopNodes.map((n, i) => (
                    <div
                      key={i}
                      className="dk-loop-node dk-anim dk-loop-wait"
                      data-step={i}
                      data-until="5"
                      style={{ left: n.x, top: n.y, '--d': `${0.35 + i * 0.08}s` }}
                    >
                      <em className="dk-loop-num">{String(i + 1).padStart(2, '0')}</em>
                      <b>{t(n.es[0], n.en[0])}</b>
                      <span>{t(n.es[1], n.en[1])}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="dk-center-abs">
              <div className="dk-loop-read dk-anim" data-from="6" data-until="6" style={{ '--d': '.2s' }}>
                {t(
                  <>Encontramos la <em className="dk-em">oportunidad.</em><br />La <em className="dk-em">construimos.</em><br /><em className="dk-em">Medimos</em> si movió el negocio.<br />La siguiente decisión, con<em className="dk-em"> evidencia.</em></>,
                  <>We find the opportunity.<br />We build it.<br />We measure whether it moved the business.<br /><em className="dk-em">The next decision, with evidence.</em></>
                )}
              </div>
            </div>
            <div className="dk-center-abs dk-loop-close">
              <h2 className="dk-hero dk-anim dk-mask" data-from="7" style={{ '--d': '.25s' }}>
                {maskLines(t(
                  ['Un proyecto se entrega.', <>La calidad <em className="dk-em">se demuestra.</em></>],
                  ['A project gets delivered.', <>Quality <em className="dk-em">is proven.</em></>]
                ))}
              </h2>
              <div className="dk-title dk-anim" data-from="7" style={{ '--d': '.55s', marginTop: '0.85em' }}>
                {t('Eso es trabajar con eien.', "That's working with eien.")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 08 · invertida ---------- */}
      <section className="dk-sec dk-multi dk-inv dk-s08" data-stages="2" data-stage="0">
        <div className="dk-stage">
          <div className="dk-frame">
            <div className="dk-mid">
              <div className="dk-statement dk-anim" style={{ maxWidth: '17em' }}>
                {t(<>Y a veces, lo más inteligente que construimos es <K>nada</K>.</>,
                   <>Sometimes the smartest thing we build is <K>nothing</K>.</>)}
              </div>
            </div>
            <div className="dk-bot">
              <div className="dk-s08-close dk-anim" data-from="1" style={{ '--d': '.4s' }}>
                {t(
                  <>Preferimos matar una mala idea en una semana<br />antes que pasar seis meses construyéndola.</>,
                  <>We'd rather kill a bad idea in one week<br />than spend six months building it.</>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

       {/* ---------- 10 · qué construimos ---------- */}
      <section className="dk-sec dk-multi dk-s11">
        <div className="dk-stage">
          <div className="dk-frame">
            <div className="dk-mid">
              <div className="dk-fitbox dk-reflow" data-w="1344" data-h="460">
                <div className="dk-poster">
                  <div className="dk-poster-scan" aria-hidden="true" />
                  {poster.map((p, i) => (
                    <span
                      key={i}
                      className={`dk-poster-item dk-poster-${p.emphasis} dk-poster-from-${p.from}`}
                      data-motion-from={p.from}
                      style={p.style}
                    >
                      {t(p.es, p.en)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 12 · modelos ---------- */}
      <section className="dk-sec dk-s10">
        <div className="dk-frame">
          <div className="dk-top">
            <div className="dk-title dk-anim">{t('Tres formas de empezar.', 'Three ways in.')}</div>
          </div>
          <div className="dk-mid">
            <div className="dk-cards">
              {cards.map((c, i) => (
                <div key={i} className={`dk-card${c.featured ? ' dk-featured' : ''} dk-anim`} style={{ '--d': `${0.15 + i * 0.12}s` }}>
                  <h4>{c.h}</h4>
                  <p>{c.p}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="dk-bot">
            <div className="dk-body dk-anim" style={{ '--d': '.7s' }}>
              {t(<>No es tercerización. Es <K>alianza</K>.</>, <>Not outsourcing. <K>Partnership</K>.</>)}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 13 · equipo ---------- */}
      <section className="dk-sec dk-s13">
        <div className="dk-frame">
          <div className="dk-mid dk-split">
            <h2 className="dk-statement dk-anim">
              {t(<>Equipos chicos.<br />Ownership alto.<br />Acelerados por <K>IA</K>.</>,
                 <>Small teams.<br />High ownership.<br /><K>AI</K> accelerated.</>)}
            </h2>
            <div className="dk-fitbox dk-reflow dk-teambox" data-w="700" data-h="560">
              <div className="dk-team">
                <svg viewBox="0 0 700 560">
                  <line className="dk-draw" pathLength="1" x1="350" y1="280" x2="350" y2="80" />
                  <line className="dk-draw" pathLength="1" x1="350" y1="280" x2="570" y2="200" />
                  <line className="dk-draw" pathLength="1" x1="350" y1="280" x2="500" y2="450" />
                  <line className="dk-draw" pathLength="1" x1="350" y1="280" x2="200" y2="450" />
                  <line className="dk-draw" pathLength="1" x1="350" y1="280" x2="130" y2="200" />
                </svg>
                {teamNodes.map((n, i) => (
                  <div key={i} className="dk-tnode dk-anim" style={{ ...n.pos, '--d': `${0.55 + i * 0.08}s` }}>
                    {t(n.es, n.en)}
                  </div>
                ))}
                <div className="dk-tcore dk-anim" style={{ '--d': '1s' }}>{t('UN PROBLEMA', 'ONE PROBLEM')}</div>
              </div>
            </div>
          </div>
          <div className="dk-bot">
            <div className="dk-body dk-anim" style={{ '--d': '.6s' }}>
              {t('Menos burocracia. Más construcción.', 'Less bureaucracy. More building.')}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 14 · manifiesto ---------- */}
      <section className="dk-sec dk-multi dk-s14" data-stages="2" data-stage="0">
        <div className="dk-stage">
          <div className="dk-frame">
            <div className="dk-center-abs">
              <div className="dk-statement dk-anim" data-until="0">
                {t('No construimos software.', "We're not building software.")}
              </div>
            </div>
            <div className="dk-center-abs">
              <h2 className="dk-hero dk-anim dk-mask" data-from="1" style={{ '--d': '.35s' }}>
                {maskLines(t(
                  ['Construimos la', <><K>próxima versión</K></>, 'de las empresas.'],
                  ["We're building the", <><K>next version</K> of companies.</>]
                ))}
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 15 · cierre ---------- */}
      <section className="dk-sec dk-s15">
        <div className="dk-aurora" aria-hidden="true" />
        <div className="dk-frame">
          <div className="dk-mid">
            <h2 className="dk-hero dk-anim dk-mask">
              {maskLines(t(
                ['¿Cuál es la próxima versión', <>de <K>tu empresa</K>?</>],
                ["What's the next version", <>of <K>your company</K>?</>]
              ))}
            </h2>
            <div className="dk-title dk-sig dk-anim" style={{ '--d': '.35s', marginTop: '.9em' }}>
              {t('Construyámosla.', "Let's build it.")}
            </div>
            <div className="dk-ctas dk-anim" style={{ '--d': '.6s' }}>
              <CalendlyLink
                className="dk-cta dk-cta-primary dk-magnet"
                ctaId={CTA_IDS.DECK_CLOSE_CALENDLY}
                ctaLocation={CTA_LOCATIONS.DECK_CLOSE}
                deckLang={lang}
              >
                {t('Agendá una llamada', 'Book a call')} <span aria-hidden="true">→</span>
              </CalendlyLink>
              <Link to="/" className="dk-cta dk-cta-ghost">
                {t('Visitar el sitio', 'Visit the site')}
              </Link>
            </div>
            <div className="dk-micro dk-dim dk-anim" style={{ '--d': '.8s', marginTop: 20 }}>
              <EmailLink
                email="hola@eientech.io"
                ctaId={CTA_IDS.DECK_CLOSE_EMAIL}
                ctaLocation={CTA_LOCATIONS.DECK_CLOSE}
              >
                hola@eientech.io
              </EmailLink>
              {' · '}
              <WhatsAppLink
                ctaId={CTA_IDS.DECK_CLOSE_WHATSAPP}
                ctaLocation={CTA_LOCATIONS.DECK_CLOSE}
                message="Hola, vi el deck de eien y quiero conversar"
              >
                WhatsApp +595 994 459 583
              </WhatsAppLink>
            </div>
          </div>
          <div className="dk-end dk-anim" style={{ '--d': '.85s' }}>
            <img src={eienLogo} alt="eien" className="dk-endlogo" />
            <div className="dk-micro dk-dim">eientech.io</div>
          </div>
        </div>
      </section>

      {/* ---------- chrome ---------- */}
      <div className="dk-bar" ref={barRef} aria-hidden="true" />
      <div className="dk-beam" aria-hidden="true">
        <canvas className="dk-trailcanvas" ref={trailRef} />
        <div className="dk-orb" ref={orbRef}>
          <i className="dk-orb-core" />
        </div>
      </div>

      {rotateHint && (
        <div className="dk-rotate" role="status">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="7" y="3" width="10" height="18" rx="2" />
            <path d="M20.5 9.5a9 9 0 0 0-3-4M3.5 14.5a9 9 0 0 0 3 4" />
            <path d="M20.5 9.5h-2.6m2.6 0V6.9M3.5 14.5h2.6m-2.6 0v2.6" />
          </svg>
          <div>
            <strong>{t('Girá tu pantalla para verlo mejor', 'Rotate your screen for the best view')}</strong>
            <span>{t('o seguí scrolleando así — también funciona', 'or just keep scrolling — it works too')}</span>
          </div>
          <button onClick={() => setRotateHint(false)} aria-label={t('Cerrar', 'Dismiss')}>✕</button>
        </div>
      )}
    </div>
  );
}
